interface Rail {
	heading: string;
	body: string;
}

interface PagePlaceholderProps {
	title: string;
	subtitle: string;
	endpoint: string;
	previewQuote?: string;
	rails?: Rail[];
	callout?: string[];
}

/**
 * Shared placeholder shape for the 5 active dashboard rails. Documents what
 * the surface will look like and which Worker endpoint feeds it. v0 ships
 * skeletons — real widgets land as the consumer / aggregator pipeline
 * produces data.
 */
export function PagePlaceholder({
	title,
	subtitle,
	endpoint,
	previewQuote,
	rails,
	callout,
}: PagePlaceholderProps) {
	return (
		<div style={styles.root}>
			<header style={styles.header}>
				<h1 style={styles.title}>{title}</h1>
				<p style={styles.subtitle}>{subtitle}</p>
				<code style={styles.endpoint}>{endpoint}</code>
			</header>

			{previewQuote && (
				<blockquote style={styles.quote}>
					<span style={styles.quoteMarker}>preview</span>
					{previewQuote}
				</blockquote>
			)}

			{callout && (
				<div style={styles.callout}>
					{callout.map((line, i) => (
						<p key={i} style={styles.calloutLine}>
							{line}
						</p>
					))}
				</div>
			)}

			{rails && (
				<div style={styles.rails}>
					{rails.map((r, i) => (
						<div key={i} style={styles.rail}>
							<h3 style={styles.railHeading}>{r.heading}</h3>
							<p style={styles.railBody}>{r.body}</p>
						</div>
					))}
				</div>
			)}

			<footer style={styles.cohortFooter}>
				Cohort minimum k=5 (k=10 cross-tab and substitution).
				Days delayed: 1. No real-time. Read{" "}
				<a href="https://aivision.space/methodology" style={{ color: "var(--accent)" }} target="_blank" rel="noopener">
					how we count this
				</a>
				.
			</footer>
		</div>
	);
}

const styles: Record<string, React.CSSProperties> = {
	root: { maxWidth: 960, display: "flex", flexDirection: "column", gap: 24 },
	header: { display: "flex", flexDirection: "column", gap: 8 },
	title: { margin: 0, fontSize: 28, fontWeight: 600 },
	subtitle: { margin: 0, color: "var(--muted)", fontSize: 15 },
	endpoint: {
		fontFamily: "ui-monospace, SFMono-Regular, monospace",
		background: "var(--card-bg)",
		border: "1px solid var(--border)",
		padding: "4px 8px",
		borderRadius: 4,
		fontSize: 12,
		alignSelf: "flex-start",
		color: "var(--muted)",
	},
	quote: {
		margin: 0,
		padding: "16px 20px",
		background: "var(--card-bg)",
		borderLeft: "3px solid var(--accent)",
		borderRadius: 4,
		fontStyle: "italic",
	},
	quoteMarker: {
		display: "block",
		fontStyle: "normal",
		textTransform: "uppercase",
		fontSize: 10,
		letterSpacing: 0.5,
		color: "var(--muted)",
		marginBottom: 8,
	},
	callout: {
		background: "rgba(184,92,0,0.06)",
		border: "1px solid var(--warn)",
		padding: 16,
		borderRadius: 6,
		display: "flex",
		flexDirection: "column",
		gap: 6,
	},
	calloutLine: { margin: 0, fontSize: 13 },
	rails: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
		gap: 16,
	},
	rail: {
		background: "var(--card-bg)",
		border: "1px solid var(--border)",
		padding: 20,
		borderRadius: 8,
		display: "flex",
		flexDirection: "column",
		gap: 8,
	},
	railHeading: { margin: 0, fontSize: 14, fontWeight: 600 },
	railBody: { margin: 0, color: "var(--muted)", fontSize: 13 },
	cohortFooter: {
		fontSize: 12,
		color: "var(--muted)",
		borderTop: "1px solid var(--border)",
		paddingTop: 16,
	},
};
